/* ALTON — product rail.

   The rail is a real scroll container (overflow-x: scroll), so in principle
   a trackpad should move it with no JS at all. In practice macOS often
   claims a two-finger horizontal swipe for browser back/forward before the
   element receives it — the row then looks frozen to the trackpad while
   still responding to a drag, which is exactly the reported symptom.

   So horizontal wheel deltas are handled EXPLICITLY below rather than left
   to native routing. Vertical wheel is never touched: the page scrolls
   normally over the rail, so the section cannot trap the scroll.

   Inputs: trackpad (wheel), drag, touch (native), keyboard.
*/
(function () {
  'use strict';

  var DRAG_THRESHOLD = 5;    /* px before a press counts as a drag  */
  var FRICTION = 0.94;       /* per frame; higher = longer coast    */
  var MIN_VELOCITY = 0.12;   /* px/frame below which coasting stops */

  function bind(rail) {
    if (rail.dataset.railBound) return;
    rail.dataset.railBound = '1';

    var down = false, dragging = false;
    var startX = 0, startScroll = 0;
    var lastX = 0, lastT = 0, velocity = 0, glide = null;

    function stopGlide() {
      if (glide) { cancelAnimationFrame(glide); glide = null; }
    }

    /* --- trackpad / horizontal wheel -------------------------------------
       Driven directly instead of relying on the browser to route it.
       preventDefault also stops the swipe becoming back/forward navigation,
       which is the usual reason a horizontal trackpad gesture does nothing
       over a scrollable row.

       The axis test is loose on purpose: a real trackpad swipe is never
       perfectly horizontal, and requiring |deltaX| > |deltaY| rejects most
       genuine sideways gestures. */
    rail.addEventListener('wheel', function (e) {
      var dx = e.deltaX;
      var dy = e.deltaY;

      /* shift+wheel is the mouse equivalent of a sideways swipe */
      if (e.shiftKey && Math.abs(dx) < Math.abs(dy)) dx = dy;

      var sideways = Math.abs(dx) > 1 && Math.abs(dx) > Math.abs(dy) * 0.4;
      if (!sideways) return;   /* vertical — let the page scroll */

      var max = rail.scrollWidth - rail.clientWidth;
      if (max <= 1) return;

      stopGlide();

      /* Only swallow the event while there is rail left to travel, so the
         page still scrolls once either end is reached. */
      if ((dx > 0 && rail.scrollLeft < max) || (dx < 0 && rail.scrollLeft > 0)) {
        e.preventDefault();
        rail.scrollLeft = Math.max(0, Math.min(max, rail.scrollLeft + dx));
      }
    }, { passive: false });

    /* --- drag ------------------------------------------------------------ */
    rail.addEventListener('dragstart', function (e) { e.preventDefault(); });

    rail.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return;   /* native touch is better */
      if (e.button !== 0) return;

      stopGlide();
      down = true;
      dragging = false;
      velocity = 0;
      startX = lastX = e.clientX;
      startScroll = rail.scrollLeft;
      lastT = performance.now();
    });

    rail.addEventListener('pointermove', function (e) {
      if (!down) return;

      var dx = e.clientX - startX;

      if (!dragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        dragging = true;
        rail.classList.add('is-dragging');
        if (rail.setPointerCapture) {
          try { rail.setPointerCapture(e.pointerId); } catch (err) {}
        }
      }

      rail.scrollLeft = startScroll - dx;

      var now = performance.now();
      var dt = now - lastT;
      if (dt > 0) {
        velocity = velocity * 0.7 + ((lastX - e.clientX) / dt * 16) * 0.3;
        lastX = e.clientX;
        lastT = now;
      }

      if (e.cancelable) e.preventDefault();
    });

    function coast() {
      velocity *= FRICTION;
      if (Math.abs(velocity) < MIN_VELOCITY) { glide = null; return; }

      var before = rail.scrollLeft;
      rail.scrollLeft += velocity;
      if (rail.scrollLeft === before) { glide = null; return; }  /* hit an end */

      glide = requestAnimationFrame(coast);
    }

    function release() {
      if (!down) return;
      down = false;
      if (!dragging) return;

      rail.classList.remove('is-dragging');

      if (performance.now() - lastT > 90) velocity = 0;

      if (Math.abs(velocity) > MIN_VELOCITY) {
        stopGlide();
        glide = requestAnimationFrame(coast);
      }
    }

    rail.addEventListener('pointerup', release);
    rail.addEventListener('pointercancel', release);
    window.addEventListener('pointerup', release);

    rail.addEventListener('click', function (e) {
      if (!dragging) return;
      dragging = false;
      e.preventDefault();
      e.stopPropagation();
    }, true);

    rail.addEventListener('touchstart', stopGlide, { passive: true });

    rail.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      e.preventDefault();
      stopGlide();

      var card = rail.querySelector('.pcard');
      var gap = parseFloat(getComputedStyle(rail).columnGap) || 18;
      var step = card ? card.getBoundingClientRect().width + gap : rail.clientWidth * 0.8;

      rail.scrollBy({ left: e.key === 'ArrowRight' ? step : -step, behavior: 'smooth' });
    });
  }

  function init() { document.querySelectorAll('.arail').forEach(bind); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
